import { useMediaQuery } from '../../hooks/useMediaQuery';

/**
 * The panel's numbers, in px. The one source: the breakpoints below are built from them, and
 * AssistantPanel.css repeats them as custom properties on `:root` (assistantLayout.test.ts holds the
 * two together). The CSS has no media queries of its own; it keys off the classes the panel sets
 * from these queries.
 */
export const ASSISTANT_LAYOUT = {
  /**
   * The least the page beside a docked panel keeps, nav included: the 232px nav plus 640px of
   * content. 640 is about the narrowest the shell's pages (a header, a toolbar, a table with a few
   * columns) read well at; any less and the page is better covered than squeezed.
   */
  pageMin: 872,
  /** The chat column: the docked panel's width with nothing shown, and the chat beside an answer. */
  chat: 400,
  /** The least an answer column (a player list, a draft form) gets beside the chat. */
  viewMin: 480,
  /** The widest the panel gets side by side. */
  splitMax: 1060,
  /** The widest the docked panel gets when the chat and the answer are tabs. */
  tabbedMax: 640,
  /** The widest the panel gets while it covers the page (it is the full width below this). */
  coverMax: 480,
} as const;

/** The narrowest window the panel docks in: the page's minimum plus the chat. */
export const ASSISTANT_DOCK_MIN_WIDTH = ASSISTANT_LAYOUT.pageMin + ASSISTANT_LAYOUT.chat;

/** The narrowest window the chat and an answer sit side by side in, with the page's minimum beside them. */
export const ASSISTANT_SPLIT_MIN_WIDTH = ASSISTANT_DOCK_MIN_WIDTH + ASSISTANT_LAYOUT.viewMin;

/** From this width the chat and the answer sit side by side; below it they are tabs. */
export const ASSISTANT_SPLIT_QUERY = `(min-width: ${ASSISTANT_SPLIT_MIN_WIDTH}px)`;

/** From this width the panel is docked (the page gives up its width); below it the panel covers the page. */
export const ASSISTANT_DOCK_QUERY = `(min-width: ${ASSISTANT_DOCK_MIN_WIDTH}px)`;

export const useAssistantLayout = (): { wide: boolean; docked: boolean } => ({
  wide: useMediaQuery(ASSISTANT_SPLIT_QUERY),
  docked: useMediaQuery(ASSISTANT_DOCK_QUERY),
});
